// components/LinkedInPostModal.jsx
import { useState } from "react"
import { X, Copy, Check, ExternalLink } from "lucide-react"
import toast from "react-hot-toast"

const ShareJobPostModal = ({
  isOpen,
  onClose,
  jobListing,
  orgId,
  jobId,
  platform = "linkedin",
}) => {
  const [copied, setCopied] = useState(false)

  if (!isOpen) return null

  // Platform configurations
  const platformConfig = {
    linkedin: {
      name: "LinkedIn",
      color: "#0A66C2",
      hoverColor: "#004182",
      url: "https://www.linkedin.com/job-posting",
      logo: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
      instructions: (
        <>
          When posting this job on LinkedIn, add this link in the{" "}
          <span className="font-medium text-gray-900">"Manage Applicants"</span>{" "}
          section to track applications.
        </>
      ),
    },
    naukri: {
      name: "Naukri.com",
      color: "#1B6AA5",
      hoverColor: "#154d7a",
      url: "https://www.naukri.com/mnjuser/postjob",
      logo: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <text
            x="50%"
            y="50%"
            dominantBaseline="middle"
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
          >
            N
          </text>
        </svg>
      ),
      instructions: (
        <>
          When posting on Naukri.com, use the{" "}
          <span className="font-medium text-gray-900">
            "Apply on Company Website"
          </span>{" "}
          option and add this link. Candidates will be redirected to your portal
          to complete their application.
        </>
      ),
    },
    indeed: {
      name: "Indeed",
      color: "#2164F3",
      hoverColor: "#1850d4",
      url: "https://employers.indeed.com/",
      logo: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <text
            x="50%"
            y="50%"
            dominantBaseline="middle"
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
          >
            i
          </text>
        </svg>
      ),
      instructions: (
        <>
          When posting on Indeed, disable{" "}
          <span className="font-medium text-gray-900">"Indeed Apply"</span> in
          the Application method section and add this link as your{" "}
          <span className="font-medium text-gray-900">"Website URL"</span> to
          redirect candidates to your portal.
        </>
      ),
    },
    glassdoor: {
      name: "Glassdoor",
      color: "#0CAA41",
      hoverColor: "#0a8a35",
      url: "https://employers.glassdoor.com/",
      logo: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <text
            x="50%"
            y="50%"
            dominantBaseline="middle"
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
          >
            G
          </text>
        </svg>
      ),
      instructions: (
        <>
          When posting on Glassdoor, set up{" "}
          <span className="font-medium text-gray-900">"Apply Now"</span> (not
          "Easy Apply") to redirect candidates to this link on your company
          careers page where they'll complete the application.
        </>
      ),
    },
  }

  const config = platformConfig[platform]

  // Generate the application link
  const applicationLink = `https://resolut.co.in/jobs/${jobId}?source=${platform}`

  const handleCopyLink = () => {
    navigator.clipboard.writeText(applicationLink)
    setCopied(true)
    toast.success("Link copied!")
    setTimeout(() => setCopied(false), 2000)
  }

  const handleOpenPlatform = () => {
    window.open(config.url, "_blank")
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: config.color }}
              >
                {config.logo}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Post to {config.name}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {jobListing.role}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 -mr-1"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          {/* Instructions */}
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              {config.instructions}
            </p>
          </div>

          {/* Steps for Naukri */}
          {platform === "naukri" && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-xs font-medium text-gray-700 mb-2">
                How it works:
              </p>
              <ol className="text-xs text-gray-600 space-y-1.5 list-decimal list-inside">
                <li>Post your job on Naukri.com</li>
                <li>Enable "Apply on Company Website" option</li>
                <li>Add the tracking link below as your company portal URL</li>
                <li>Candidates click "Apply on Company Website" on Naukri</li>
                <li>
                  They're redirected to your system to complete the application
                </li>
              </ol>
            </div>
          )}

          {/* Steps for Indeed */}
          {platform === "indeed" && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-xs font-medium text-gray-700 mb-2">
                How to set up:
              </p>
              <ol className="text-xs text-gray-600 space-y-1.5 list-decimal list-inside">
                <li>Go to your Employer Dashboard on Indeed</li>
                <li>Start posting a new job or edit an existing one</li>
                <li>Scroll to the "Application method" section</li>
                <li>Turn off "Receive applications on Indeed"</li>
                <li>
                  Paste the tracking link below in the "Website URL" field
                </li>
              </ol>
            </div>
          )}

          {/* Steps for Glassdoor */}
          {platform === "glassdoor" && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-xs font-medium text-gray-700 mb-2">
                How it works:
              </p>
              <ol className="text-xs text-gray-600 space-y-1.5 list-decimal list-inside">
                <li>Post your job on Glassdoor Employer Dashboard</li>
                <li>Set up "Apply Now" button (not "Easy Apply")</li>
                <li>
                  Add the tracking link below as your company careers page URL
                </li>
                <li>Candidates click "Apply Now" on Glassdoor</li>
                <li>
                  They're redirected to your portal to complete the application
                </li>
              </ol>
            </div>
          )}

          {/* Application Link */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {platform === "naukri"
                ? "Company Portal URL"
                : platform === "indeed"
                ? "Website URL"
                : platform === "glassdoor"
                ? "Company Careers Page URL"
                : "Tracking Link"}
            </label>
            <div className="flex gap-2">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 min-w-0">
                <code className="text-xs text-gray-600 font-mono block truncate">
                  {applicationLink}
                </code>
              </div>
              <button
                onClick={handleCopyLink}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all flex-shrink-0 ${
                  copied
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
              >
                {copied ? (
                  <>
                    <Check size={16} />
                    <span className="text-sm">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    <span className="text-sm">Copy</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Includes{" "}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                ?source={platform}
              </code>{" "}
              to identify applicants
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center gap-3">
          <button
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleOpenPlatform}
            className="flex items-center gap-2 px-5 py-2.5 text-white rounded-lg transition-colors font-medium text-sm"
            style={{
              backgroundColor: config.color,
            }}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = config.hoverColor)
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = config.color)
            }
          >
            Open {config.name}
            <ExternalLink size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShareJobPostModal